import * as THREE from 'three';

/* ═══════════════════════════════════════════════════════════════════════════
   Le matériau des POWER-UPS qui tombent — un JETON lumineux, pas un orbe de tir.

   Les ramassables n'étaient pas rendus jusqu'ici (la monnaie `Cell` attend son
   économie au J7). Les power-ups, eux, DOIVENT se voir : on court les chercher
   sous le feu. Ils vivent dans la scène principale, à `Layer.Pickups`, donc ils
   BLOOMENT — un butin qui glow se remarque au coin de l'œil pendant l'esquive.

   La couleur vient de `instanceColor` (une par TYPE de bonus, posée par la vue),
   ce qui permet un seul matériau, un seul programme, pour tous les jetons. La
   silhouette — cœur blanc chaud + couronne colorée + halo — se lit d'emblée comme
   un objet à ramasser, distinct des orbes concentriques des projectiles.

   Même anti-aliasing ANALYTIQUE que les projectiles (§4.4) : `fwidth` + `smoothstep`,
   net à toute résolution, aucune texture à charger. Injecté par `onBeforeCompile`
   sur un MeshBasicMaterial pour garder la gestion d'instancing de three.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Gain émissif HDR : pousse les zones vives au-dessus du seuil de bloom (0,85)
 *  pour que le jeton rayonne franchement. Même logique que les tirs joueur. */
const PICKUP_EMISSIVE = 2.6;

export function makePickupMaterial(): THREE.MeshBasicMaterial {
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    depthWrite: false,
    // Toujours visible : un power-up masqué derrière un ennemi serait un butin
    // qu'on ne sait pas aller chercher.
    depthTest: false,
    blending: THREE.AdditiveBlending,
  });

  // Force la varying `vUv` (aucune texture, sinon three ne la déclare pas) et
  // active la couleur par instance (`instanceColor` → `vColor`).
  mat.defines = { ...(mat.defines ?? {}), USE_UV: '' };

  mat.onBeforeCompile = (shader) => {
    /* Point d'injection : AVANT `<specularmap_fragment>`, comme le matériau de
       projectile. À `<opaque_fragment>` il serait trop tard — `outgoingLight` est
       déjà figé et le jeton sortirait blanc (le piège documenté sur les balles).
       `<color_fragment>`, qui a déjà multiplié `diffuseColor.rgb` par la couleur
       d'instance, tourne AVANT nous : `diffuseColor.rgb` porte donc ici la teinte
       du TYPE de bonus. */
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <specularmap_fragment>',
      `#include <specularmap_fragment>
{
  // Coordonnée centrée sur le quad : (0,0) au centre, ±0,5 aux bords.
  vec2 uv = vUv - 0.5;
  float d = length(uv) * 2.0;          // 0 au centre, 1 au bord
  float fw = max(fwidth(d), 1e-5) * 1.5;

  vec3 body = diffuseColor.rgb;        // couleur du TYPE (via instanceColor)

  // Cœur blanc chaud, couronne colorée, halo doux : trois zones nettes qui
  // disent « jeton à ramasser » d'un coup d'œil.
  float core = 1.0 - smoothstep(0.24 - fw, 0.24 + fw, d);
  float ring = smoothstep(0.46 - fw, 0.46 + fw, d) - smoothstep(0.82 - fw, 0.82 + fw, d);
  float glow = (1.0 - smoothstep(0.82 - fw, 1.0 + fw, d)) * 0.35;

  vec3 zone = mix(body, vec3(1.0), core);   // centre → blanc
  float mask = clamp(core + ring + glow, 0.0, 1.0);

  diffuseColor.rgb = zone * ${PICKUP_EMISSIVE.toFixed(2)};
  diffuseColor.a *= mask;
}`,
    );
  };

  // ⚠️ Sans clé distincte, three réutiliserait le programme d'un MeshBasicMaterial
  // ordinaire et l'injection serait ignorée EN SILENCE. Une seule variante de
  // jeton → une seule clé, un seul programme au warm-up.
  mat.customProgramCacheKey = () => 'pickup-v1';

  return mat;
}
